/* eslint-disable  sonarjs/no-duplicate-string */
import {
	CloseOutlined,
	EditOutlined,
	EyeOutlined,
	CheckOutlined,
	StopOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Input, message, Modal, Row, Spin, Typography, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Text as AtomText } from 'atoms';
import {
	fetchAdhocRequestDetail,
	fetchAdhocRequestDetailV2,
	deleteAdhocRequestV2,
	approveAdhocRequestV2,
	rejectAdhocRequestV2,
} from 'store/advancePaymentSlice';
import { getSignedUrl } from 'store/commonSlice';
import { addUser } from 'store/pageSlice';
import { getApprovalDetailsV2 } from 'store/approvalSliceV2';
import { reuploadPoR, POR_DOC_ENTITY } from 'store/approvalSlice';
import { getFormatedDayDateAndTime } from 'utils';
import { WarningTriangle } from 'utils/Svg';
import { getUIVersion } from 'utils/router-static-data';

const CITY_MANAGER_USER_TYPE = 'city_manager';
const ADMIN_USER_TYPE = 'admin';
const ADHOC_REQUEST_ROUTE = '/trips/adhoc-request';

// PoR summary status (por_approval_status) is lowercase from the API.
const POR_STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

const { Title, Text } = Typography;

const PageContainer = styled(Box)`
	padding: 24px;
	background: ${(props) => (props.fromApproval ? '#fff' : '#f5f5f5')};
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const HeaderRight = styled.div`
	display: flex;
	gap: 12px;
`;

const RequestId = styled(Title)`
	margin: 0 !important;
	font-size: 20px;
	font-weight: 600;
`;

const CloseIconButton = styled(Button)`
	border: none;
	box-shadow: none;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;

	&:hover {
		background-color: #f5f5f5;
	}
`;

const ActionButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e8e8e8;
`;

const RejectButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
	}
`;

const ApproveButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #52c41a;
	color: #fff;
	border: 1.5px solid #52c41a;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #73d13d;
		border-color: #73d13d;
	}

	&:active {
		background: #389e0d;
		border-color: #389e0d;
	}
`;

const DeleteButton = styled(Button)`
	color: #dc3545;
	border-color: #dc3545;

	&:hover {
		color: #fff;
		background-color: #dc3545;
		border-color: #dc3545;
	}
`;

const EditButton = styled(Button)`
	color: #fff;
	background-color: #18405d;
	border-color: #18405d;

	&:hover {
		background-color: #0f2d42;
		border-color: #0f2d42;
	}
`;

const ApproveButton = styled(Button)`
	color: #fff;
	background-color: #28a745;
	border-color: #28a745;

	&:hover {
		background-color: #218838;
		border-color: #218838;
	}
`;
const VPApproveButton = styled(Button)`
	color: #fff;
	background-color: #e9ae47ff;
	border-color: #e9ae47ff;

	&:hover {
		background-color: #e9ae47ff;
		border-color: #e9ae47ff;
	}
`;

const RejectButton = styled(Button)`
	color: #fff;
	background-color: #dc3545;
	border-color: #dc3545;

	&:hover {
		background-color: #c82333;
		border-color: #c82333;
	}
`;

const DetailsCard = styled(Card)`
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-bottom: 24px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 4px;
`;

const FieldValue = styled(Text)`
	font-size: 14px;
	color: #000;
	font-weight: 500;
	display: block;
`;

const StatusBadge = styled.span`
	padding: 4px 12px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	background-color: ${(props) => {
		if (props.status === 'Rejected') return '#ffebee';
		if (props.status === 'Approved') return '#e8f5e9';
		return '#fff3e0';
	}};
	color: ${(props) => {
		if (props.status === 'Rejected') return '#c62828';
		if (props.status === 'Approved') return '#2e7d32';
		return '#f57c00';
	}};
`;

const DeleteModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
		text-align: center;
		padding: 40px 20px 20px;
	}
	.ant-modal-body {
		padding: 0;
	}
	.ant-modal-footer {
		border-top: none;
		padding: 20px 0 0;
		text-align: center;
	}
`;

const ModalIconContainer = styled.div`
	display: flex;
	justify-content: center;
	margin-bottom: 24px;
`;

const ModalTitle = styled.div`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	margin-bottom: 32px;
`;

const RejectionRemarksInput = styled(Input.TextArea)`
	margin-bottom: 24px;
	border-radius: 4px;

	&:focus {
		border-color: #18405d;
		box-shadow: 0 0 0 2px rgba(24, 64, 93, 0.2);
	}
`;

const ModalButtonContainer = styled.div`
	display: flex;
	gap: 16px;
	justify-content: center;
`;

const NoButton = styled(Button)`
	width: 160px;
	height: 40px;
	border: 1px solid #000;
	color: #000;
	background: #fff;
	border-radius: 4px;
	font-size: 16px;
	font-weight: 500;

	&:hover {
		border-color: #000;
		color: #000;
		background: #f5f5f5;
	}
`;

const YesButton = styled(Button)`
	width: 160px;
	height: 40px;
	background: #18405d;
	color: #fff;
	border: none;
	border-radius: 4px;
	font-size: 16px;
	font-weight: 500;

	&:hover {
		background: #0f2d42;
	}
`;

const { TextArea } = Input;

const NO_DOCUMENT_UPLOADED = 'No document uploaded';
// Mirrors adhoc_request_update_schema.rejection_reason (minLength 10) so a reason that
// cannot pass the server is stopped here, with the rule visible, instead of after a
// round-trip that does not explain itself.
const REJECTION_REASON_MIN = 10;
const REJECTION_REASON_MAX = 500;

function rejectionReasonError(value) {
	const trimmed = (value || '').trim();
	if (!trimmed) return 'Rejection reason is required';
	if (trimmed.length < REJECTION_REASON_MIN)
		return `Rejection reason must be at least ${REJECTION_REASON_MIN} characters`;
	if (trimmed.length > REJECTION_REASON_MAX)
		return `Rejection reason cannot exceed ${REJECTION_REASON_MAX} characters`;
	return '';
}
const APPROVALS_AD_HOC_REQUEST_PATH = '/approvals/ad-hoc-request';

// eslint-disable-next-line sonarjs/cognitive-complexity
function AdhocRequestDetails() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { me = {} } = useSelector((state) => state.auth);
	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showApproveModal, setShowApproveModal] = useState(false);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectionRemarks, setRejectionRemarks] = useState('');
	// Field-level message, shown under the textarea. The API already names the field and
	// says what is wrong; a generic toast threw that away and left the approver guessing.
	const [rejectionError, setRejectionError] = useState('');
	const [showVPApproveModal, setShowVPApproveModal] = useState(false);
	const [showVPApproveAt80Modal, setShowVPApproveAt80Modal] = useState(false);
	const [showVPRejectModal, setShowVPRejectModal] = useState(false);
	const [vpRejectionRemarks, setVpRejectionRemarks] = useState('');
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [previewLoading, setPreviewLoading] = useState(false);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');
	const [deletionReason, setDeletionReason] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	const [reuploadingPor, setReuploadingPor] = useState(false);

	// Check if accessed from approval page
	const isApprovalRoute = location.pathname.startsWith('/approvals/ad-hoc-request');
	const fromApproval = location.state?.fromApproval || isApprovalRoute;
	const isPorFeatureV2 = getUIVersion() === 'new';
	const entityIdFromState = location.state?.entityId || null;
	const isV2UI = getUIVersion() === 'new';

	// Check if user is citymanager or admin
	const currentUser = me?.user_type || '';
	console.log('currentUser', currentUser);
	// debugger;
	const isAuthorizedUser =
		currentUser === CITY_MANAGER_USER_TYPE || currentUser === ADMIN_USER_TYPE;
	const isPendingApproval = details?.status === 'Pending Approval';
	// Raised from the intracity trip form rather than the adhoc-request form. Those
	// submissions have no advance at all, and their locations are free-text addresses
	// from the trip's route rather than pincodes.
	const isIntracityMarginRequest = !!details?.request_info?.pending_intracity_trip;
	const locationText = (pincode, city, state, addressText) => {
		if (pincode) return `${pincode}, ${city || ''}, ${state || ''}`;
		return addressText || '-';
	};
	const showVpApproveButton = details?.show_vp_approval_button ?? false;
	const marginPerceage =
		details?.client_rate && details?.margin
			? ((parseFloat(details.margin) / parseFloat(details?.client_rate)) * 100).toFixed(0)
			: 0;
	const advancePercentage =
		details?.advance_amount_requested && details?.advance_amount_requested > 0
			? (
					(parseFloat(details?.advance_amount_requested) /
						parseFloat(details?.vendor_rate)) *
					100
			  ).toFixed(0)
			: 0;
	useEffect(() => {
		if (id) {
			if (isApprovalRoute) {
				fetchApprovalDetails();
			} else {
				fetchDetails();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isApprovalRoute]);

	const fetchApprovalDetails = async () => {
		setLoading(true);
		try {
			const approvalData = await dispatch(getApprovalDetailsV2({ approval_id: id }));

			if (!approvalData) {
				message.error('Approval not found');
				return;
			}

			const entityId =
				approvalData.approval?.entity_id ||
				approvalData.entity_details?.entity_id ||
				approvalData.entity_details?.id ||
				entityIdFromState;

			if (entityId) {
				const response = isV2UI
					? await dispatch(fetchAdhocRequestDetailV2({ id: entityId }))
					: await dispatch(fetchAdhocRequestDetail({ id: entityId }));

				if (response?.rows && response.rows.length > 0) {
					setDetails(response.rows[0]);
				} else {
					message.error('Request not found');
				}
			} else {
				message.error('Entity ID not found in approval details');
			}
		} catch (error) {
			console.error('Error fetching approval details:', error);
			message.error('Failed to fetch approval details');
		} finally {
			setLoading(false);
		}
	};

	const fetchDetails = async () => {
		setLoading(true);
		try {
			// Use V2 API for new UI, V1 for old UI
			const response = isV2UI
				? await dispatch(fetchAdhocRequestDetailV2({ id }))
				: await dispatch(fetchAdhocRequestDetail({ id }));

			if (response?.rows && response.rows.length > 0) {
				setDetails(response.rows[0]);
			} else {
				message.error('Request not found');
				navigate(ADHOC_REQUEST_ROUTE);
			}
		} catch (error) {
			console.error('Error fetching details:', error);
			message.error('Failed to fetch request details');
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = () => {
		// A request raised from the intracity trip form carries that whole submission in
		// request_info. Editing it on the adhoc-request form would show the wrong fields
		// (advance, supervisor) and, worse, could not touch the vehicle, driver, km or
		// times the user actually entered — so send them back to the form they filled in.
		const marginStash = details?.request_info?.pending_intracity_trip;
		if (marginStash?.payload) {
			navigate('/trip/add-intracity', {
				state: {
					marginRequestId: details.id,
					marginRequestPayload: marginStash.payload,
				},
			});
			return;
		}
		navigate(`${ADHOC_REQUEST_ROUTE}/create?id=${details.id}`, {
			state: {
				isEditMode: true,
				requestData: details,
			},
		});
	};
	const handlePorReupload = async (file) => {
		if (!details?.trip_id) {
			message.error('Trip reference not available for PoR re-upload');
			return false;
		}
		if (file.size > 5 * 1024 * 1024) {
			message.error('File must be under 5MB');
			return false;
		}
		setReuploadingPor(true);
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: { entity: POR_DOC_ENTITY, keys: [file.name] },
				})
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('Failed to get signed URL');
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			await dispatch(
				reuploadPoR({ trip_id: details.trip_id, document_key: fileData.filename })
			);
			message.success('Proof of Request re-submitted. Awaiting Control Tower approval.');
			if (isApprovalRoute) {
				fetchApprovalDetails();
			} else {
				fetchDetails();
			}
		} catch (err) {
			message.error('PoR re-upload failed');
		} finally {
			setReuploadingPor(false);
		}
		return false;
	};

	const handleDelete = () => {
		setShowDeleteModal(true);
		setDeletionReason(''); // Clear deletion reason when opening modal
	};

	const handleApprove = () => {
		setShowApproveModal(true);
	};

	const handleApproveCancel = () => {
		setShowApproveModal(false);
	};

	const handleReject = () => {
		setShowRejectModal(true);
	};

	const handleRejectCancel = () => {
		setRejectionError('');
		setShowRejectModal(false);
		setRejectionRemarks('');
	};

	const handleDeleteCancel = () => {
		setShowDeleteModal(false);
		setDeletionReason(''); // Clear deletion reason when canceling
	};

	const handleDeleteConfirm = async () => {
		try {
			if (isV2UI) {
				// Use V2 API for new UI - deletion reason is required
				if (!deletionReason.trim()) {
					message.error('Deletion reason is required');
					return;
				}
				await dispatch(
					deleteAdhocRequestV2({
						id: details.id,
						deletion_reason: deletionReason.trim(),
					})
				);
				message.success('Request deleted successfully');
				setShowDeleteModal(false);
				setDeletionReason(''); // Clear deletion reason after success
				navigate(ADHOC_REQUEST_ROUTE);
			} else {
				// Use V1 API for old UI
				const payload = {
					update_type: 'delete_adhoc_request',
					data: {
						id: details.id,
					},
				};

				await dispatch(addUser({ data: payload, setErrors: () => {}, isEditMode: true }));

				message.success('Request deleted successfully');
				setShowDeleteModal(false);
				navigate(ADHOC_REQUEST_ROUTE);
			}
		} catch (error) {
			console.error('Error deleting request:', error);
			message.error('Failed to delete request');
		}
	};

	const handleApproveClick = () => {
		setApproveModalVisible(true);
		setComments('');
	};

	const handleRejectClick = () => {
		setRejectModalVisible(true);
		setRejectionReason('');
	};

	const refreshCurrentPage = () => window.location.reload();

	// Constants for error messages
	const ADHOC_REQUEST_ID_NOT_FOUND = 'Adhoc request ID not found';

	// Helper function to handle V1 approval
	const handleV1Approval = async () => {
		// Use V1 API for old UI - use addUser with update_type: "adhoc_request"
		if (!details?.id) {
			message.error(ADHOC_REQUEST_ID_NOT_FOUND);
			return false;
		}

		const payload = {
			update_type: 'adhoc_request',
			data: {
				id: details.id,
				status: 'Approved',
			},
		};

		const result = await dispatch(
			addUser({
				data: payload,
				setErrors: () => {},
				isEditMode: true,
			})
		);

		if (result?.status === 'success') {
			setApproveModalVisible(false);
			setComments('');
			message.success('Request approved successfully');
			refreshCurrentPage();
			return true;
		}
		return false;
	};

	const handleApproveConfirm = async () => {
		setActionLoading(true);
		try {
			if (isV2UI) {
				if (!details?.id) {
					message.error('Adhoc request ID not found');
					return;
				}

				const result = await dispatch(
					approveAdhocRequestV2({
						id: details.id,
						status: 'Approved',
						comments,
					})
				);

				if (result?.status === 'success') {
					setApproveModalVisible(false);
					setComments('');
					message.success('Request approved successfully');
					refreshCurrentPage();
				}
			} else {
				await handleV1Approval();
			}
		} catch (error) {
			console.error('Error approving request:', error);
			message.error('Failed to approve request');
		} finally {
			setActionLoading(false);
		}
	};

	const handleVPApprove = () => {
		setShowVPApproveModal(true);
	};

	const handleVPApproveConfirm = async () => {
		try {
			const payload = {
				update_type: 'vp_approval',
				data: {
					id: details.id,
					action: 'approve',
				},
			};
			await dispatch(addUser({ data: payload, setErrors: () => {}, isEditMode: true }));
			message.success('VP Approved successfully');
			setShowVPApproveModal(false);
			refreshCurrentPage();
		} catch (error) {
			console.error('Error in VP Approve:', error);
			message.error('Failed to VP Approve request');
		}
	};

	const handleVPApproveAt80 = () => {
		setShowVPApproveAt80Modal(true);
	};

	const handleVPApproveAt80Confirm = async () => {
		try {
			const payload = {
				update_type: 'vp_approval',
				data: {
					id: details.id,
					action: 'approve_80',
				},
			};
			await dispatch(addUser({ data: payload, setErrors: () => {}, isEditMode: true }));
			message.success('VP Approved at 80% successfully');
			setShowVPApproveAt80Modal(false);
			refreshCurrentPage();
		} catch (error) {
			console.error('Error in VP Approve at 80%:', error);
			message.error('Failed to VP Approve at 80% request');
		}
	};

	const handleVPReject = () => {
		setShowVPRejectModal(true);
	};

	const handleVPRejectConfirm = async () => {
		if (!vpRejectionRemarks.trim()) {
			message.warning('Please provide rejection remarks');
			return;
		}
		try {
			const payload = {
				update_type: 'vp_approval',
				data: {
					id: details.id,
					action: 'reject',
					rejection_reason: vpRejectionRemarks.trim(),
				},
			};
			await dispatch(addUser({ data: payload, setErrors: () => {}, isEditMode: true }));
			message.success('VP Rejected successfully');
			setShowVPRejectModal(false);
			setVpRejectionRemarks('');
			refreshCurrentPage();
		} catch (error) {
			console.error('Error in VP Reject:', error);
			message.error('Failed to VP Reject request');
		}
	};

	// Helper function to handle rejection success
	const handleRejectionSuccess = () => {
		setRejectModalVisible(false);
		setRejectionReason('');
		message.success('Request rejected successfully');
		refreshCurrentPage();
	};

	// Helper function to validate rejection inputs (shared for V1 and V2)
	const validateRejectionInputs = (rejectionText) => {
		if (!details?.id) {
			message.error(ADHOC_REQUEST_ID_NOT_FOUND);
			return false;
		}
		if (!rejectionText?.trim()) {
			message.error('Rejection reason is required');
			return false;
		}
		return true;
	};

	// Helper function to validate V2 rejection inputs
	const validateV2Rejection = () => validateRejectionInputs(rejectionReason);

	// Helper function to handle V2 rejection
	const handleV2Rejection = async () => {
		if (!validateV2Rejection()) {
			return;
		}

		const result = await dispatch(
			rejectAdhocRequestV2({
				id: details.id,
				rejection_reason: rejectionReason,
			})
		);

		if (result?.status === 'success') {
			handleRejectionSuccess();
		}
	};

	// Helper function to validate V1 rejection inputs
	const validateV1Rejection = () => validateRejectionInputs(rejectionReason);

	// Helper function to handle V1 rejection
	const handleV1Rejection = async () => {
		if (!validateV1Rejection()) {
			return;
		}

		const payload = {
			update_type: 'adhoc_request',
			data: {
				id: details.id,
				status: 'Rejected',
				rejection_remarks: rejectionReason.trim(),
			},
		};

		const result = await dispatch(
			addUser({
				data: payload,
				setErrors: () => {},
				isEditMode: true,
			})
		);

		if (result?.status === 'success') {
			handleRejectionSuccess();
		}
	};

	const handleRejectConfirm = async () => {
		setActionLoading(true);
		try {
			if (isV2UI) {
				await handleV2Rejection();
			} else {
				await handleV1Rejection();
			}
		} catch (error) {
			console.error('Error rejecting request:', error);
			message.error('Failed to reject request');
		} finally {
			setActionLoading(false);
		}
	};

	// Handler for old flow reject modal (uses rejectionRemarks state)
	const handleOldFlowRejectConfirm = async () => {
		// Validate here first: the rule is known, so a reason that cannot pass should never
		// reach the server. Shown against the field, not as a toast.
		const localError = rejectionReasonError(rejectionRemarks);
		if (localError) {
			setRejectionError(localError);
			return;
		}

		if (!details?.id) {
			message.error(ADHOC_REQUEST_ID_NOT_FOUND);
			return;
		}

		setRejectionError('');
		// API.common rejects the promise on any HTTP error, so a 422 lands in the catch
		// below rather than returning a result. The 422 handler still runs setErrors first,
		// so the message is captured here and read back after the throw.
		let fieldError = '';
		try {
			if (isV2UI) {
				const result = await dispatch(
					rejectAdhocRequestV2({
						id: details.id,
						rejection_reason: rejectionRemarks.trim(),
						setErrors: (errors) => {
							fieldError = errors?.rejection_reason || '';
						},
					})
				);

				if (result?.status === 'success') {
					setShowRejectModal(false);
					setRejectionRemarks('');
					message.success('Request rejected successfully');
					refreshCurrentPage();
				} else {
					message.error('Failed to reject request');
				}
			} else {
				// Use V1 API for old UI
				const payload = {
					update_type: 'adhoc_request',
					data: {
						id: details.id,
						status: 'Rejected',
						rejection_remarks: rejectionRemarks.trim(),
					},
				};

				const result = await dispatch(
					addUser({
						data: payload,
						setErrors: () => {},
						isEditMode: true,
					})
				);

				if (result?.status === 'success') {
					setShowRejectModal(false);
					setRejectionRemarks('');
					message.success('Request rejected successfully');
					refreshCurrentPage();
				} else {
					message.error('Failed to reject request');
				}
			}
		} catch (error) {
			console.error('Error rejecting request:', error);
			if (fieldError) {
				// The API named the field and said what is wrong — show that against the
				// input, and show nothing else. A generic toast on top helps nobody.
				setRejectionError(fieldError);
			} else {
				message.error('Failed to reject request');
			}
		}
	};

	const getFilenameFromUrl = (url) => {
		if (!url) return '';
		return url.startsWith('s3://') ? url.replace('s3://logitrack/', '') : url;
	};

	const getFileExtension = (filename) => {
		if (!filename) return '';
		return filename.split('.').pop().toLowerCase();
	};

	const handlePreview = async (url, title) => {
		if (!url) {
			message.error('Document not available');
			return;
		}
		if (title) {
			setPreviewTitle(title);
		}
		setPreviewLoading(true);
		try {
			const filename = getFilenameFromUrl(url);
			const data = {
				url_type: 'view',
				data: {
					entity: 'trip',
					keys: [filename],
				},
			};

			const response = await dispatch(getSignedUrl(data));
			if (response?.data && response.data[0]?.url) {
				setPreviewUrl(response.data[0].url);
				setPreviewTitle(filename);
				setPreviewVisible(true);
			} else {
				message.error('Failed to get preview URL');
			}
		} catch (error) {
			console.error('Preview error:', error);
			message.error('Failed to preview document');
		} finally {
			setPreviewLoading(false);
		}
	};

	const renderPreviewContent = () => {
		const fileExt = getFileExtension(previewTitle);

		if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)) {
			return (
				<img
					alt={previewTitle}
					style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
					src={previewUrl}
				/>
			);
		}
		if (fileExt === 'pdf') {
			return (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<div style={{ marginBottom: '20px' }}>
						<p style={{ fontSize: '16px', marginBottom: '10px' }}>
							PDF Preview: {previewTitle}
						</p>
						<p style={{ color: '#666', marginBottom: '20px' }}>
							For better PDF viewing experience, please open in a new tab
						</p>
					</div>
					<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
						<Button
							type="primary"
							onClick={() => window.open(previewUrl, '_blank')}
							size="large"
						>
							Open PDF in New Tab
						</Button>
					</div>
					<div
						style={{
							marginTop: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '4px',
						}}
					>
						<iframe
							title={previewTitle}
							src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
							width="100%"
							height="400px"
							style={{ border: 'none' }}
							onError={() => {
								// If iframe fails, it will be hidden and user can use buttons above
							}}
						/>
					</div>
				</div>
			);
		}
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				<p>Preview not available for this file type.</p>
				<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
					Open in New Tab
				</Button>
			</div>
		);
	};

	if (loading) {
		return (
			<PageContainer>
				<Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer>
				<Text>Request not found</Text>
			</PageContainer>
		);
	}

	return (
		<PageContainer fromApproval={fromApproval}>
			<HeaderContainer>
				<HeaderLeft>
					<RequestId level={4}>
						{fromApproval ? `Request ID: ${details.id}` : `Req ID : ${details.id}`}
					</RequestId>
				</HeaderLeft>
				<HeaderRight>
					{fromApproval ? (
						<CloseIconButton
							icon={<CloseOutlined />}
							onClick={() => navigate(APPROVALS_AD_HOC_REQUEST_PATH)}
						/>
					) : (
						<>
							<DeleteButton icon={<CloseOutlined />} onClick={handleDelete}>
								Cancel
							</DeleteButton>
							{isAuthorizedUser && isPendingApproval && (
								<>
									<ApproveButton icon={<CheckOutlined />} onClick={handleApprove}>
										Approve
									</ApproveButton>
									<RejectButton icon={<StopOutlined />} onClick={handleReject}>
										Reject
									</RejectButton>
								</>
							)}
							{/* 
						showVpApproveButton
						//if advance > 80 
					*/}
							{showVpApproveButton && (
								<>
									<ApproveButton
										icon={<CheckOutlined />}
										onClick={handleVPApprove}
									>
										VP Approve
									</ApproveButton>
									{advancePercentage > 80 && marginPerceage > 5 ? (
										<VPApproveButton
											icon={<CheckOutlined />}
											onClick={handleVPApproveAt80}
										>
											VP Approve at 80%
										</VPApproveButton>
									) : (
										<RejectButton
											icon={<StopOutlined />}
											onClick={handleVPReject}
										>
											VP Reject
										</RejectButton>
									)}
								</>
							)}
							{/* Edit is hidden for an intracity margin request: the submission
							    lives in request_info and must be edited on the intracity trip
							    form, which cannot yet be prefilled from it (vehicle_type and
							    city are required there but absent from the stash). Rejecting
							    and resubmitting is the working path until that is built. */}
							<EditButton
								style={{ display: isIntracityMarginRequest ? 'none' : undefined }}
								icon={<EditOutlined />}
								onClick={handleEdit}
								disabled={
									((details.status === 'Approved' ||
										details.status === 'Trip Created') &&
										!details.can_generate_proforma_invoice) ||
									details.status === 'Rejected'
								}
							>
								EDIT
							</EditButton>
						</>
					)}
				</HeaderRight>
			</HeaderContainer>

			<DetailsCard>
				<Row gutter={[24, 16]}>
					{/* Left Column */}
					<Col span={8}>
						<FieldLabel>Client</FieldLabel>
						<FieldValue>{details.client_name || '-'}</FieldValue>
					</Col>
					{/* Middle Column */}
					<Col span={8}>
						<FieldLabel>Project</FieldLabel>
						<FieldValue>{details.project_code || '-'}</FieldValue>
					</Col>
					{/* Right Column */}
					<Col span={8}>
						<FieldLabel>Vendor</FieldLabel>
						<FieldValue>{details.vendor_name || '-'}</FieldValue>
					</Col>
				</Row>

				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					{/* Left Column */}
					<Col span={8}>
						<FieldLabel>Vehicle Type</FieldLabel>
						<FieldValue>{details.vehicle_type || '-'}</FieldValue>
					</Col>
					{/* Middle Column */}
					<Col span={8}>
						<FieldLabel>Source Location*</FieldLabel>
						<FieldValue>
							{locationText(
								details.source_pincode,
								details.source_city,
								details.source_state,
								details.address?.source
							)}
						</FieldValue>
					</Col>
					{/* Right Column */}
					<Col span={8}>
						<FieldLabel>Destination Location*</FieldLabel>
						<FieldValue>
							{locationText(
								details.destination_pincode,
								details.destination_city,
								details.destination_state,
								details.address?.destination
							)}
						</FieldValue>
					</Col>
				</Row>

				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					{/* Left Column */}
					<Col span={8}>
						<FieldLabel>Trip Date</FieldLabel>
						<FieldValue>
							{details.expected_trip_date
								? `${
										getFormatedDayDateAndTime(
											details.expected_trip_date,
											'trip_date'
										).fomatedDate
								  } ${
										getFormatedDayDateAndTime(
											details.expected_trip_date,
											'trip_date'
										).formatedTimeAMPM
								  }`
								: '-'}
						</FieldValue>
					</Col>
					{/* Middle Column */}
					<Col span={8}>
						<FieldLabel>Temperature Log *</FieldLabel>
						<FieldValue>
							{details.is_temperature_log_required ? 'Yes' : 'No'}
						</FieldValue>
					</Col>
					{/* Right Column */}
					<Col span={8}>
						<FieldLabel>Business Unit*</FieldLabel>
						<FieldValue>{details.business_unit_name || '--'}</FieldValue>
					</Col>
				</Row>

				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					{/* Left Column — the intracity trip form captures no supervisor, so the
					    field is always empty on these requests */}
					{!isIntracityMarginRequest && (
						<Col span={8}>
							<FieldLabel>Supervisor Name*</FieldLabel>
							<FieldValue>
								{details.supervisor_name || details.assigned_to_name || '-'}
							</FieldValue>
						</Col>
					)}
					{/* Middle Column */}
					<Col span={8}>
						<FieldLabel>Request Status*</FieldLabel>
						<FieldValue>
							<StatusBadge status={details.status}>
								{details?.is_high_value_advance
									? details?.vp_approval_status ?? details.status
									: details.status}
							</StatusBadge>
						</FieldValue>
					</Col>
					{/* Right Column — no advance exists on an intracity trip submission */}
					{!isIntracityMarginRequest && (
						<Col span={8}>
							<FieldLabel>Advance Status*</FieldLabel>
							<FieldValue>
								<StatusBadge status={details.advance_status}>
									{details.advance_status || 'Unpaid'}
								</StatusBadge>
							</FieldValue>
						</Col>
					)}
					{/* Takes the slot Advance Status vacates, so the row stays at 24 and no
					    other adhoc request page shifts. These requests show no source or
					    destination pincode, which makes City the useful locator. */}
					{isIntracityMarginRequest && (
						<Col span={8}>
							<FieldLabel>City</FieldLabel>
							<FieldValue>{details.city || 'Not available'}</FieldValue>
						</Col>
					)}
				</Row>
			</DetailsCard>

			<DetailsCard>
				<Row gutter={[24, 16]}>
					<Col span={8}>
						<FieldLabel>Client Rate*</FieldLabel>
						<FieldValue>₹ {parseFloat(details.client_rate || 0).toFixed(2)}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Vendor Rate*</FieldLabel>
						<FieldValue>₹ {parseFloat(details.vendor_rate || 0).toFixed(2)}</FieldValue>
					</Col>
					{/* UTR is the advance payment reference — not applicable here either */}
					{!isIntracityMarginRequest && (
						<Col span={8}>
							<FieldLabel>UTR Number</FieldLabel>
							<FieldValue>{details.utr_number || '-'}</FieldValue>
						</Col>
					)}
				</Row>

				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Margin*</FieldLabel>
						<FieldValue>
							₹ {parseFloat(details.margin || 0).toFixed(2)} (
							{details.client_rate && details.margin
								? (
										(parseFloat(details.margin) /
											parseFloat(details.client_rate)) *
										100
								  ).toFixed(0)
								: '0'}
							%)
						</FieldValue>
					</Col>
					{!isIntracityMarginRequest && (
						<Col span={8}>
							<FieldLabel>Advance*</FieldLabel>
							<FieldValue>
								₹ {parseFloat(details.advance_amount_requested || 0).toFixed(2)} (
								{details.vendor_rate && details.advance_amount_requested
									? (
											(parseFloat(details.advance_amount_requested) /
												parseFloat(details.vendor_rate)) *
											100
									  ).toFixed(0)
									: '0'}
								%)
							</FieldValue>
						</Col>
					)}
					<Col span={8}>
						<FieldLabel>Rejected Reason</FieldLabel>
						<FieldValue
							style={{ color: details.rejection_remarks ? '#dc3545' : '#000' }}
						>
							{details.rejection_remarks || '-'}
						</FieldValue>
					</Col>
				</Row>

				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Proof Of Request</FieldLabel>
						{isPorFeatureV2 && details.por_approval_status && (
							<FieldValue>
								<StatusBadge
									status={
										POR_STATUS_LABEL[details.por_approval_status] ||
										details.por_approval_status
									}
								>
									{POR_STATUS_LABEL[details.por_approval_status] ||
										details.por_approval_status}
								</StatusBadge>
							</FieldValue>
						)}
						{!(isPorFeatureV2 && details.por_approval_status === 'rejected') &&
							(details.proof_of_request_url ? (
								<Button
									type="primary"
									icon={<EyeOutlined />}
									onClick={() =>
										handlePreview(
											details.proof_of_request_url,
											'Proof of Request'
										)
									}
									loading={previewLoading}
									style={{
										marginTop: 8,
										background: '#1890ff',
										borderColor: '#1890ff',
									}}
								>
									Preview Document
								</Button>
							) : (
								<Text style={{ marginTop: 8, display: 'block' }}>
									{NO_DOCUMENT_UPLOADED}
								</Text>
							))}
						{isPorFeatureV2 && details.por_approval_status === 'rejected' && (
							<Box
								style={{
									marginTop: 12,
									padding: '10px 12px',
									background: '#fff1f0',
									border: '1px solid #ffa39e',
									borderRadius: 6,
								}}
							>
								<Text style={{ color: '#cf1322', display: 'block', fontSize: 12 }}>
									PoR rejected
									{details.por_rejection_reason
										? `: ${details.por_rejection_reason}`
										: ''}
								</Text>
								<Upload
									beforeUpload={handlePorReupload}
									maxCount={1}
									showUploadList={false}
									accept=".pdf,.jpg,.jpeg,.png"
								>
									<Button
										size="small"
										icon={<UploadOutlined />}
										loading={reuploadingPor}
										style={{ marginTop: 8 }}
									>
										Upload New PoR
									</Button>
								</Upload>
							</Box>
						)}
					</Col>
					{/* Pro-forma invoicing belongs to the advance flow, which an intracity
					    trip submission has no part in */}
					{!isIntracityMarginRequest && (
						<>
							<Col span={8}>
								<FieldLabel>Pro-Forma Invoice</FieldLabel>
								{details.vendor_proforma_invoice_url ? (
									<Button
										type="primary"
										icon={<EyeOutlined />}
										onClick={() =>
											handlePreview(
												details.vendor_proforma_invoice_url,
												'Pro-Forma Invoice'
											)
										}
										loading={previewLoading}
										style={{
											marginTop: 8,
											background: '#1890ff',
											borderColor: '#1890ff',
										}}
									>
										Preview Document
									</Button>
								) : (
									<Text style={{ marginTop: 8, display: 'block' }}>
										{NO_DOCUMENT_UPLOADED}
									</Text>
								)}
							</Col>
							<Col span={8}>
								<FieldLabel>Pro-Forma Invoice Number</FieldLabel>
								<FieldValue style={{ marginTop: 8, display: 'block' }}>
									{details.vendor_proforma_invoice_number ||
										details.proforma_invoice_number ||
										NO_DOCUMENT_UPLOADED}
								</FieldValue>
							</Col>
						</>
					)}
				</Row>
			</DetailsCard>

			<DetailsCard>
				<Row gutter={[24, 16]}>
					<Col span={24}>
						<FieldLabel>Remarks</FieldLabel>
						<FieldValue>{details.remarks || '-'}</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{/* Action Buttons for Approval Context */}
			{fromApproval && (
				<ActionButtonContainer>
					<RejectButtonStyled
						icon={<CloseOutlined />}
						onClick={handleRejectClick}
						disabled={actionLoading}
					>
						Reject
					</RejectButtonStyled>
					<ApproveButtonStyled
						icon={<CheckOutlined />}
						onClick={handleApproveClick}
						disabled={actionLoading}
					>
						Approve
					</ApproveButtonStyled>
				</ActionButtonContainer>
			)}

			<DeleteModal
				open={showDeleteModal}
				onCancel={handleDeleteCancel}
				footer={null}
				closable={false}
				centered
				width={500}
			>
				<ModalIconContainer>
					<WarningTriangle />
				</ModalIconContainer>
				<ModalTitle>Do you want to delete the Request ?</ModalTitle>
				{getUIVersion() === 'new' && (
					<RejectionRemarksInput
						placeholder="Please provide deletion reason (required)"
						value={deletionReason}
						onChange={(e) => setDeletionReason(e.target.value)}
						rows={4}
						maxLength={500}
						showCount
					/>
				)}
				<ModalButtonContainer>
					<NoButton onClick={handleDeleteCancel}>No</NoButton>
					<YesButton onClick={handleDeleteConfirm}>Yes</YesButton>
				</ModalButtonContainer>
			</DeleteModal>

			<DeleteModal
				open={showApproveModal}
				onCancel={handleApproveCancel}
				footer={null}
				closable={false}
				centered
				width={500}
			>
				<ModalIconContainer>
					<WarningTriangle />
				</ModalIconContainer>
				<ModalTitle>Do you want to approve this Request ?</ModalTitle>
				<ModalButtonContainer>
					<NoButton onClick={handleApproveCancel}>No</NoButton>
					<YesButton onClick={handleApproveConfirm}>Yes</YesButton>
				</ModalButtonContainer>
			</DeleteModal>

			<DeleteModal
				open={showRejectModal}
				onCancel={handleRejectCancel}
				footer={null}
				closable={false}
				centered
				width={500}
			>
				<ModalIconContainer>
					<WarningTriangle />
				</ModalIconContainer>
				<ModalTitle>Do you want to reject this Request ?</ModalTitle>
				<RejectionRemarksInput
					placeholder={`Please provide rejection remarks (minimum ${REJECTION_REASON_MIN} characters)`}
					value={rejectionRemarks}
					onChange={(e) => {
						setRejectionRemarks(e.target.value);
						// Clear as soon as it becomes valid, so the message does not linger
						// while the approver is still typing.
						if (rejectionError) setRejectionError(rejectionReasonError(e.target.value));
					}}
					rows={4}
					maxLength={REJECTION_REASON_MAX}
					showCount
					status={rejectionError ? 'error' : undefined}
				/>
				<Text
					style={{
						display: 'block',
						marginTop: 4,
						fontSize: 12,
						color: rejectionError ? '#c62828' : '#666',
					}}
				>
					{rejectionError || `Minimum ${REJECTION_REASON_MIN} characters`}
				</Text>
				<ModalButtonContainer>
					<NoButton onClick={handleRejectCancel}>No</NoButton>
					<YesButton
						disabled={!!rejectionReasonError(rejectionRemarks)}
						onClick={handleOldFlowRejectConfirm}
					>
						Yes
					</YesButton>
				</ModalButtonContainer>
			</DeleteModal>

			{/* VP Approve Confirmation Modal */}
			<DeleteModal
				open={showVPApproveModal}
				onCancel={() => setShowVPApproveModal(false)}
				footer={null}
				closable={false}
				centered
				width={500}
			>
				<ModalIconContainer>
					<WarningTriangle />
				</ModalIconContainer>
				<ModalTitle>Do you want to VP Approve this Request?</ModalTitle>
				<ModalButtonContainer>
					<NoButton onClick={() => setShowVPApproveModal(false)}>No</NoButton>
					<YesButton onClick={handleVPApproveConfirm}>Yes</YesButton>
				</ModalButtonContainer>
			</DeleteModal>

			{/* VP Approve at 80% Confirmation Modal */}
			<DeleteModal
				open={showVPApproveAt80Modal}
				onCancel={() => setShowVPApproveAt80Modal(false)}
				footer={null}
				closable={false}
				centered
				width={500}
			>
				<ModalIconContainer>
					<WarningTriangle />
				</ModalIconContainer>
				<ModalTitle>Do you want to VP Approve this Request at 80%?</ModalTitle>
				<ModalButtonContainer>
					<NoButton onClick={() => setShowVPApproveAt80Modal(false)}>No</NoButton>
					<YesButton onClick={handleVPApproveAt80Confirm}>Yes</YesButton>
				</ModalButtonContainer>
			</DeleteModal>

			{/* VP Reject Confirmation Modal */}
			<DeleteModal
				open={showVPRejectModal}
				onCancel={() => {
					setShowVPRejectModal(false);
					setVpRejectionRemarks('');
				}}
				footer={null}
				closable={false}
				centered
				width={500}
			>
				<ModalIconContainer>
					<WarningTriangle />
				</ModalIconContainer>
				<ModalTitle>Do you want to VP Reject this Request?</ModalTitle>
				<RejectionRemarksInput
					placeholder="Please provide rejection remarks (required)"
					value={vpRejectionRemarks}
					onChange={(e) => setVpRejectionRemarks(e.target.value)}
					rows={4}
					maxLength={500}
					showCount
				/>
				<ModalButtonContainer>
					<NoButton
						onClick={() => {
							setShowVPRejectModal(false);
							setVpRejectionRemarks('');
						}}
					>
						No
					</NoButton>
					<YesButton onClick={handleVPRejectConfirm}>Yes</YesButton>
				</ModalButtonContainer>
			</DeleteModal>

			<Modal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={() => {
					setPreviewVisible(false);
					setPreviewUrl('');
					setPreviewTitle('');
				}}
				centered
				width={getFileExtension(previewTitle) === 'pdf' ? 1000 : 800}
				bodyStyle={{ textAlign: 'center', background: '#fff' }}
				destroyOnClose
			>
				{renderPreviewContent()}
			</Modal>

			{/* Approve Modal */}
			<Modal
				title={null}
				open={approveModalVisible}
				onCancel={() => {
					setApproveModalVisible(false);
					setComments('');
				}}
				footer={null}
				centered
				width={400}
			>
				<Box style={{ textAlign: 'center' }}>
					<AtomText fontSize="20px" fontWeight="600" color="#000" display="block" mb={2}>
						Are you sure?
					</AtomText>
					<AtomText fontSize="14px" color="#666" display="block" mb={3}>
						This trip will be approved and you won&apos;t be able to revert this change.
					</AtomText>
					<Box mb={3} style={{ textAlign: 'left' }}>
						<AtomText
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments (Optional)
						</AtomText>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter any comments for this approval..."
							maxLength={1000}
							showCount
						/>
					</Box>
					<Box style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
						<Button
							onClick={() => {
								setApproveModalVisible(false);
								setComments('');
							}}
							disabled={actionLoading}
							style={{
								borderColor: '#1890ff',
								color: '#1890ff',
								background: '#fff',
								minWidth: '100px',
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							onClick={handleApproveConfirm}
							loading={actionLoading}
							disabled={actionLoading}
							style={{ minWidth: '100px' }}
						>
							Approve
						</Button>
					</Box>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Ad-Hoc Request"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !rejectionReason.trim(),
					danger: true,
				}}
			>
				<Box mb={3}>
					<AtomText fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject this ad-hoc request?
					</AtomText>
				</Box>
				<Box>
					<AtomText fontSize="14px" color="#333" mb={1} display="block">
						Rejection Reason <AtomText color="#ff4d4f">*</AtomText>
					</AtomText>
					<TextArea
						rows={4}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="Enter reason for rejection..."
						maxLength={1000}
						showCount
						status={!rejectionReason.trim() && rejectionReason !== '' ? 'error' : ''}
					/>
					{!rejectionReason.trim() && rejectionReason !== '' && (
						<AtomText fontSize="12px" color="#ff4d4f" mt={1} display="block">
							Rejection reason is required
						</AtomText>
					)}
				</Box>
			</Modal>
		</PageContainer>
	);
}

export default AdhocRequestDetails;
